<script setup>
import { i18n } from 'boot/i18n';
import FilterChipsBar from 'components/UI/FilterChipsBar.vue';
import { useDialogPluginComponent } from 'quasar';
import {
  confirmDestructiveDialog,
  HrConfirmBtn,
  HrDestructiveBtn,
  HrDismissBtn,
  HrFormDialog,
  HrFormSectionHeader,
  HrTextField,
} from 'src/design-system';

import { useEditCohortDialog } from '../composables/useEditCohortDialog';
import { COHORT_DIALOG_ACTIONS } from '../constants/cohortDialogActions';

defineOptions({ name: 'EditCohortDialog' });

const props = defineProps({
  cohort: {
    type: Object,
    required: true,
  },
  cohortEditAttemptId: {
    type: String,
    required: true,
  },
  assignedClientsCount: {
    type: Number,
    default: 0,
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const {
  editCohort,
  cohortFilters,
  canAddFilter,
  isEditing,
  isSubmitDisabled,
  rules,
  touched,
  setTouched,
  filtersError,
  touchFilters,
  prepareCohortForSave,
  onFiltersChanged,
  filterCriteria,
  columnGroups,
} = useEditCohortDialog({
  cohort: props.cohort,
  cohortEditAttemptId: props.cohortEditAttemptId,
});

const dialogTitle = isEditing
  ? i18n.t('settings.careModel.cohorts.edit.editTitle')
  : i18n.t('settings.careModel.cohorts.edit.addTitle');

async function onSubmit() {
  if (!touched.value) {
    return;
  }

  touchFilters();
  const isValid = await dialogRef.value?.validate();
  if (!isValid || filtersError.value !== null) {
    return;
  }

  onDialogOK({
    action: COHORT_DIALOG_ACTIONS.SAVE,
    cohort: prepareCohortForSave(),
  });
}

/**
 * On confirmation, unassign the clients from the cohort and then delete the cohort.
 */
function onDelete() {
  const count = props.assignedClientsCount;

  const onConfirm = () => {
    onDialogOK({
      action: COHORT_DIALOG_ACTIONS.DELETE,
      cohort: editCohort.value,
    });
  };

  if (count > 0) {
    confirmDestructiveDialog(
      i18n.t('settings.careModel.cohorts.edit.dialogTitleDeleteConfirm'),
      i18n.tc('settings.careModel.cohorts.edit.dialogMessageDeleteConfirmAssigned', count, { count }),
      onConfirm,
      { confirmLabel: i18n.t('settings.careModel.cohorts.edit.buttonDeleteCohort') },
    );
    return;
  }

  confirmDestructiveDialog(
    i18n.t('common.dialogTitleConfirm'),
    i18n.t('common.notifyDeleteConfirm'),
    onConfirm,
    { confirmLabel: i18n.t('common.buttonDelete') },
  );
}
</script>

<template>
  <hr-form-dialog
    ref="dialogRef"
    size="sm"
    :title="dialogTitle"
    @hide="onDialogHide"
    @close="onDialogCancel"
    @submit="onSubmit"
  >
    <div class="row q-col-gutter-md">
      <hr-text-field
        v-model="editCohort.cohortName"
        :label="i18n.t('cohorts.fields.cohortName')"
        :rules="rules.cohortName"
        maxlength="100"
        lazy-rules
        hide-bottom-space
        class="col-12"
        @update:model-value="setTouched()"
      />
      <hr-form-section-header
        :label="i18n.t('cohorts.fields.filters')"
        class="col-12"
      />
      <div class="col-12">
        <p class="hr-body-small q-mt-none q-mb-sm">
          {{ i18n.t('settings.careModel.cohorts.edit.filtersCaption') }}
        </p>
        <filter-chips-bar
          :filters="cohortFilters"
          :filter-criteria="filterCriteria"
          :filter-groups="columnGroups"
          :hide-add="!canAddFilter"
          :add-label="i18n.t('settings.careModel.cohorts.edit.addFilters')"
          :custom-title="i18n.t('settings.careModel.cohorts.edit.filtersDialogTitle')"
          @update:filters="onFiltersChanged"
        />
        <p
          v-if="filtersError !== null"
          class="hr-body-small hr-text-error q-mt-sm q-mb-none"
          role="alert"
        >
          {{ filtersError }}
        </p>
      </div>
    </div>
    <template #destructive-actions>
      <hr-destructive-btn
        v-if="isEditing"
        :label="i18n.t('common.buttonDelete')"
        @click="onDelete"
      />
    </template>
    <template #actions>
      <hr-dismiss-btn
        :label="i18n.t('common.buttonCancel')"
        @click="onDialogCancel"
      />
      <hr-confirm-btn
        type="submit"
        :label="i18n.t('common.buttonSave')"
        :disable="isSubmitDisabled"
      />
    </template>
  </hr-form-dialog>
</template>
